import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, FileText, Trash2, Loader2, Library as LibraryIcon, CheckCircle2, Wrench } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const docTypes = [
  { value: "question_paper", label: "Question Paper" },
  { value: "mark_scheme", label: "Mark Scheme" },
  { value: "examiner_report", label: "Examiner Report" },
  { value: "specification", label: "Specification (admin reference)" },
];

export default function Library() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [counts, setCounts] = useState({ seed: 0, uploaded: 0 });
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ doc_type: "question_paper", paper: "1", year: 2024, session: "June" });
  const [uploading, setUploading] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [bulkSummary, setBulkSummary] = useState(null);

  const bulkUpload = async () => {
    if (!bulkFiles.length) return toast.error("Choose your PDFs or a zip first");
    setBulkProgress({ done: 0, total: bulkFiles.length, current: "Uploading files…" });
    setBulkSummary(null);
    try {
      const fd = new FormData();
      bulkFiles.forEach((f) => fd.append("files", f));
      fd.append("exam_board", "AQA");
      fd.append("qualification", "A-Level");
      fd.append("subject", "Psychology");
      const { data } = await api.post("/library/bulk-upload", fd);
      for (let i = 0; i < 900; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: job } = await api.get(`/jobs/${data.job_id}`);
        if (job.progress) setBulkProgress(job.progress);
        if (job.status === "done") {
          setBulkSummary(job.result);
          setBulkProgress(null);
          setBulkFiles([]);
          toast.success(`Bulk upload complete — ${job.result?.questions_added || 0} questions added`);
          load();
          return;
        }
        if (job.status === "error") throw new Error(job.error || "Bulk upload failed");
      }
      throw new Error("Timed out waiting for processing");
    } catch (e) {
      setBulkProgress(null);
      toast.error(errMsg(e));
    }
  };

  const load = () => api.get("/library/documents").then((r) => {
    setDocs(r.data.documents);
    setCounts({ seed: r.data.seed_questions, uploaded: r.data.uploaded_questions });
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) return toast.error("Choose a PDF file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", form.doc_type);
      fd.append("exam_board", "AQA");
      fd.append("qualification", "A-Level");
      fd.append("subject", "Psychology");
      fd.append("paper", form.paper);
      fd.append("year", form.year);
      fd.append("session", form.session);
      const { data } = await api.post("/library/upload", fd);
      toast.success(form.doc_type === "question_paper"
        ? `Extracted ${data.question_count} questions into the database!`
        : "Document stored for analysis context");
      setFile(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/library/documents/${id}`);
      toast.success("Document and its questions removed");
      load();
    } catch (e) {
      toast.error("Could not delete document");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="library-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Past Paper Library</p>
      <h1 className="text-4xl font-bold tracking-tighter">Upload Past Papers</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">Upload real AQA A-Level Psychology PDFs. The AI reads each question paper and extracts every question with topic, command word, marks and AO metadata — powering more accurate analysis, predictions and generation.</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Badge variant="secondary" data-testid="uploaded-questions-count">{counts.uploaded} questions from real papers</Badge>
        <Badge variant="outline">{counts.seed} demo seed questions{counts.uploaded > 0 ? " (replaced per paper as you upload)" : ""}</Badge>
        {isAdmin && (
          <Link to="/admin/labels" className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors" data-testid="library-admin-labels-link">
            <Wrench className="h-3 w-3" /> Reconcile subtopic labels
          </Link>
        )}
      </div>

      {isAdmin && (
        <div className="mt-8 rounded-3xl border border-primary/30 bg-primary/5 p-6" data-testid="bulk-upload-card">
          <h2 className="font-semibold tracking-tight mb-1">Bulk upload a whole folder</h2>
          <p className="text-xs text-muted-foreground mb-4 max-w-2xl">Select all your PDFs at once (or one .zip of the folder). Each file&rsquo;s type, paper number, year and session are auto-detected from the filename and contents — question papers are AI-extracted, mark schemes and examiner reports are stored as analysis context.</p>
          {!bulkProgress ? (
            <div className="flex flex-col sm:flex-row gap-3 items-start">
              <label className="rounded-2xl border-2 border-dashed border-border hover:border-primary/50 transition-colors px-6 py-4 cursor-pointer text-sm" data-testid="bulk-file-area">
                <input type="file" accept=".pdf,.zip" multiple className="hidden" data-testid="bulk-file-input"
                  onChange={(e) => setBulkFiles(Array.from(e.target.files || []))} />
                {bulkFiles.length ? (
                  <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" />{bulkFiles.length} file{bulkFiles.length > 1 ? "s" : ""} selected</span>
                ) : (
                  <span className="text-muted-foreground flex items-center gap-2"><Upload className="h-4 w-4" />Choose PDFs or a .zip</span>
                )}
              </label>
              <Button className="rounded-full glow-btn" data-testid="bulk-upload-btn" disabled={!bulkFiles.length} onClick={bulkUpload}>
                <Upload className="h-4 w-4 mr-2" />Upload & Auto-Detect All
              </Button>
            </div>
          ) : (
            <div data-testid="bulk-progress">
              <div className="flex justify-between text-xs mb-2">
                <span className="text-muted-foreground truncate pr-4">{bulkProgress.current ? `Processing: ${bulkProgress.current}` : "Working…"}</span>
                <span className="font-semibold shrink-0">{bulkProgress.done}/{bulkProgress.total}</span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-[width] duration-500"
                  style={{ width: `${Math.round((bulkProgress.done / Math.max(bulkProgress.total, 1)) * 100)}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">Question papers take 30–90s each (AI extraction) — you can leave this page and come back; processing continues in the background.</p>
            </div>
          )}
          {bulkSummary && (
            <div className="mt-4 rounded-2xl bg-secondary p-4 text-xs space-y-2" data-testid="bulk-summary">
              <p className="font-semibold text-sm">Done: {bulkSummary.questions_added} questions extracted from {bulkSummary.processed.filter((p) => p.doc_type === "question_paper").length} question papers • {bulkSummary.processed.filter((p) => p.doc_type !== "question_paper").length} mark schemes/reports stored</p>
              {bulkSummary.skipped.length > 0 && (
                <div>
                  <p className="font-medium text-amber-500 mb-1">Skipped ({bulkSummary.skipped.length}) — upload these individually with the manual form:</p>
                  {bulkSummary.skipped.map((s2, i) => <p key={i} className="text-muted-foreground">• {s2.file}: {s2.reason}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {isAdmin ? (
        <div className="rounded-3xl border border-border bg-card p-6 space-y-5 h-fit">
          <div className="space-y-1.5">
            <Label>Document type</Label>
            <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
              <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{docTypes.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Paper</Label>
              <Select value={form.paper} onValueChange={(v) => setForm({ ...form, paper: v })}>
                <SelectTrigger data-testid="upload-paper-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["1", "2", "3"].map((p) => <SelectItem key={p} value={p}>Paper {p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Year</Label><Input data-testid="upload-year-input" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || 2024 })} /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Session</Label>
              <Select value={form.session} onValueChange={(v) => setForm({ ...form, session: v })}>
                <SelectTrigger data-testid="upload-session-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["June", "November"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <label className="block rounded-2xl border-2 border-dashed border-border hover:border-primary/50 transition-colors p-8 text-center cursor-pointer" data-testid="file-drop-area">
            <input type="file" accept="application/pdf" className="hidden" data-testid="file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {file ? (
              <span className="flex items-center justify-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-500" />{file.name}</span>
            ) : (
              <span className="text-sm text-muted-foreground flex flex-col items-center gap-2"><Upload className="h-5 w-5" />Click to choose a PDF</span>
            )}
          </label>
          <p className="text-xs text-muted-foreground">Fixed to AQA A-Level Psychology (7182) for now. More boards & subjects coming later.</p>
          <Button className="w-full rounded-full glow-btn" data-testid="upload-btn" disabled={uploading || !file} onClick={upload}>
            {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Extracting with AI…</> : <><Upload className="h-4 w-4 mr-2" />Upload & Extract</>}
          </Button>
        </div>
        ) : (
        <div className="rounded-3xl border border-border bg-card p-6 h-fit" data-testid="library-admin-notice">
          <p className="text-sm font-medium mb-2">Admin access required</p>
          <p className="text-xs text-muted-foreground leading-relaxed">The past-paper library is shared across the platform, so only admin accounts can upload or remove documents. You can still browse everything that powers the analysis on the right.</p>
        </div>
        )}

        <div className="lg:col-span-2">
          {docs.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border p-16 text-center">
              <LibraryIcon className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No documents uploaded yet. The platform currently runs on demo seed data — upload real past papers to supercharge accuracy.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="library-doc-list">
              {docs.map((d) => (
                <div key={d.id} className="rounded-2xl border border-border bg-card p-5 flex items-center justify-between gap-4" data-testid={`library-doc-${d.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{d.filename}</p>
                      <p className="text-xs text-muted-foreground">{d.exam_board} {d.qualification} {d.subject} • Paper {d.paper} • {d.session} {d.year}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="secondary" className="text-xs">{docTypes.find((t) => t.value === d.doc_type)?.label}</Badge>
                    {d.doc_type === "question_paper" && <Badge className="bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/15 text-xs">{d.question_count} questions</Badge>}
                    {isAdmin && (
                    <Button variant="ghost" size="icon" data-testid={`delete-doc-${d.id}`} onClick={() => remove(d.id)} className="rounded-full text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
